import React, { useState, useEffect, useRef, useCallback } from 'react';
import AIRecommendation from '../components/AIRecommendation';

const YouTubePlayer = ({ language }) => {
  const [url, setUrl] = useState('');
  const [audioOn, setAudioOn] = useState(false);
  const [audioArmed, setAudioArmed] = useState(false);
  const [showSoundTip, setShowSoundTip] = useState(false);
  const [playlistHistory, setPlaylistHistory] = useState(() => {
    // 로컬 스토리지에서 히스토리 복원
    const saved = localStorage.getItem('playlistHistory');
    return saved ? JSON.parse(saved) : [];
  });
  const [currentSong] = useState(null);
  const playerRef = useRef(null);

  // 모바일 환경 감지
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // 유틸 함수들
  const getPlaylistId = (raw) => {
    try {
      const u = new URL(raw);
      const list = u.searchParams.get('list');
      if (list) return list;
    } catch (e) {
      // 그냥 ID만 넣었을 수도 있음
      if (/^PL|^OLAK5uy|^UU|^LL|^RD/.test(raw)) return raw.trim();
    }
    return null;
  };

  const buildEmbedURL = (playlistId) => {
    const params = new URLSearchParams({
      list: playlistId,
      autoplay: '1',
      playsinline: '1',
      loop: '1',
      rel: '0',
      modestbranding: '1',
      enablejsapi: '1'
    });
    return `https://www.youtube.com/embed/videoseries?${params.toString()}`;
  };

  const ytCmd = useCallback((func, args = []) => {
    playerRef.current?.contentWindow?.postMessage(
      JSON.stringify({ event: 'command', func, args }), 
      '*'
    );
  }, []);

  // 플레이리스트 제목 추출 (YouTube API 사용)
  const extractPlaylistTitle = (url) => {
    try {
      const urlObj = new URL(url);
      const searchParams = urlObj.searchParams;
      
      if (url.includes('playlist') && url.includes('list=')) {
        const listId = searchParams.get('list');
        if (listId) {
          // YouTube API를 사용해서 실제 플레이리스트 정보 가져오기
          // 현재는 동기적으로 처리할 수 없으므로 기존 로직 사용
          
          // API 실패 시 기존 로직 사용
          const pathSegments = urlObj.pathname.split('/').filter(Boolean);
          
          for (const segment of pathSegments) {
            const lowerSegment = segment.toLowerCase();
            
            if (lowerSegment.includes('music') || lowerSegment.includes('song') || lowerSegment.includes('audio')) {
              return '음악 플레이리스트';
            } else if (lowerSegment.includes('pop') || lowerSegment.includes('kpop') || lowerSegment.includes('jpop')) {
              return '팝 음악 플레이리스트';
            } else if (lowerSegment.includes('rock') || lowerSegment.includes('metal')) {
              return '락/메탈 음악 플레이리스트';
            } else if (lowerSegment.includes('jazz') || lowerSegment.includes('blues')) {
              return '재즈/블루스 플레이리스트';
            } else if (lowerSegment.includes('classical') || lowerSegment.includes('orchestra')) {
              return '클래식/오케스트라 플레이리스트';
            } else if (lowerSegment.includes('electronic') || lowerSegment.includes('edm') || lowerSegment.includes('techno')) {
              return '일렉트로닉/EDM 플레이리스트';
            } else if (lowerSegment.includes('hiphop') || lowerSegment.includes('rap')) {
              return '힙합/랩 플레이리스트';
            } else if (lowerSegment.includes('country') || lowerSegment.includes('folk')) {
              return '컨트리/포크 플레이리스트';
            } else if (lowerSegment.includes('reggae') || lowerSegment.includes('ska')) {
              return '레게/스카 플레이리스트';
            } else if (lowerSegment.includes('latin') || lowerSegment.includes('salsa')) {
              return '라틴 음악 플레이리스트';
            } else if (lowerSegment.includes('chill') || lowerSegment.includes('relax') || lowerSegment.includes('ambient')) {
              return '차분한/앰비언트 음악 플레이리스트';
            } else if (lowerSegment.includes('workout') || lowerSegment.includes('gym') || lowerSegment.includes('fitness')) {
              return '운동/피트니스 음악 플레이리스트';
            } else if (lowerSegment.includes('study') || lowerSegment.includes('focus') || lowerSegment.includes('concentration')) {
              return '공부/집중 음악 플레이리스트';
            } else if (lowerSegment.includes('party') || lowerSegment.includes('dance')) {
              return '파티/댄스 음악 플레이리스트';
            } else if (lowerSegment.includes('sleep') || lowerSegment.includes('lullaby')) {
              return '수면/자장가 플레이리스트';
            } else if (lowerSegment.includes('romantic') || lowerSegment.includes('love')) {
              return '로맨틱/사랑 노래 플레이리스트';
            } else if (lowerSegment.includes('nostalgic') || lowerSegment.includes('retro')) {
              return '추억/레트로 음악 플레이리스트';
            }
          }
          
          const query = searchParams.get('q') || searchParams.get('search_query');
          if (query) {
            const lowerQuery = query.toLowerCase();
            if (lowerQuery.includes('jazz')) return '재즈 플레이리스트';
            if (lowerQuery.includes('rock')) return '락 음악 플레이리스트';
            if (lowerQuery.includes('pop')) return '팝 음악 플레이리스트';
            if (lowerQuery.includes('classical')) return '클래식 플레이리스트';
            if (lowerQuery.includes('electronic')) return '일렉트로닉 플레이리스트';
            if (lowerQuery.includes('hiphop')) return '힙합 플레이리스트';
            if (lowerQuery.includes('country')) return '컨트리 플레이리스트';
            if (lowerQuery.includes('latin')) return '라틴 음악 플레이리스트';
            if (lowerQuery.includes('chill')) return '차분한 음악 플레이리스트';
            if (lowerQuery.includes('workout')) return '운동 음악 플레이리스트';
            if (lowerQuery.includes('study')) return '공부 음악 플레이리스트';
          }
          
          return 'YouTube 플레이리스트';
        }
      }
      
      return 'YouTube 플레이리스트';
    } catch (e) {
      return 'YouTube 플레이리스트';
    }
  };

  // YouTube API로 플레이리스트 제목 가져오기
  const fetchPlaylistTitle = async (playlistId) => {
    try {
      const response = await fetch(
        `https://www.googleapis.com/youtube/v3/playlists?part=snippet&id=${playlistId}&key=${process.env.REACT_APP_YOUTUBE_API_KEY}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.items && data.items.length > 0) {
          return data.items[0].snippet.title;
        }
      }
    } catch (error) {
      console.error('YouTube API Error:', error);
    }
    return null;
  };

  // 플레이리스트 로드
  const loadPlaylist = (src) => {
    const pid = getPlaylistId(src);
    if (!pid) {
      alert(language === 'ko' 
        ? '플레이리스트 URL 또는 ID를 확인해 주세요. (list=... 필요)' 
        : 'Please check the playlist URL or ID. (list=... required)'
      );
      return;
    }
    const embed = buildEmbedURL(pid);
    if (playerRef.current) {
      playerRef.current.src = embed;
    }
    localStorage.setItem('lastPlaylist', src);
    setAudioOn(false);
    setShowSoundTip(true);
    
    // 임시 제목으로 먼저 추가
    const tempTitle = extractPlaylistTitle(src);
    
    // 플레이리스트 히스토리에 추가
    const newPlaylist = {
      id: pid,
      title: tempTitle,
      url: src,
      timestamp: new Date().toISOString(),
      playCount: 1
    };
    
         // setCurrentPlaylist(newPlaylist); // 현재 사용하지 않음
    setPlaylistHistory(prev => {
      const existing = prev.find(p => p.id === pid);
      if (existing) {
        // 기존 플레이리스트인 경우 재생 횟수 증가
        const updated = {
          ...existing,
          playCount: (existing.playCount || 0) + 1,
          timestamp: new Date().toISOString()
        };
        const filtered = prev.filter(p => p.id !== pid);
        const newHistory = [updated, ...filtered].slice(0, 10);
        localStorage.setItem('playlistHistory', JSON.stringify(newHistory));
        return newHistory;
      } else {
        // 새로운 플레이리스트인 경우 추가
        const newHistory = [newPlaylist, ...prev].slice(0, 10);
        localStorage.setItem('playlistHistory', JSON.stringify(newHistory));
        return newHistory;
      }
    });

    // YouTube API로 실제 제목 가져와서 업데이트
    fetchPlaylistTitle(pid).then(realTitle => {
      if (realTitle) {
        setPlaylistHistory(prev => {
          const updated = prev.map(p => 
            p.id === pid ? { ...p, title: realTitle } : p
          );
          localStorage.setItem('playlistHistory', JSON.stringify(updated));
          return updated;
        });
      }
    });
  };

  // 데모 플레이리스트
  const loadDemo = () => {
    const demoUrl = 'https://www.youtube.com/playlist?list=PLtuRlaWtDzbsva8iJAkP5UzvmiGZckNA1';
    setUrl(demoUrl);
    loadPlaylist(demoUrl);
  };

  // 오디오 토글
  const updateAudioIcon = useCallback(() => {
    if (audioArmed) return;
    setAudioArmed(true);
    ytCmd('unMute');
    ytCmd('playVideo');
    setAudioOn(true);
    setShowSoundTip(false);
  }, [audioArmed, ytCmd]);

  const toggleAudio = () => {
    if (!audioArmed) {
      updateAudioIcon();
      return;
    }
    const newAudioState = !audioOn;
    setAudioOn(newAudioState);
    if (newAudioState) {
      ytCmd('unMute');
      ytCmd('playVideo');
      setShowSoundTip(false);
    } else {
      ytCmd('mute');
      ytCmd('pauseVideo');
    }
  };

  // iframe 로드 후 자동재생 상태 확인 (간단한 방법)
  useEffect(() => {
    if (playerRef.current && playerRef.current.src) {
      // iframe이 로드되면 잠시 후 자동재생 상태 확인
      const timer = setTimeout(() => {
        // 자동재생이 성공했다고 가정하고 오디오 상태 업데이트
        setAudioOn(true);
        setAudioArmed(true);
        setShowSoundTip(false);
      }, 2000); // 2초 후 자동재생 성공으로 간주

      return () => clearTimeout(timer);
    }
  }, [playerRef.current?.src]);

  // 키보드/터치 이벤트
  useEffect(() => {
    const handleInteraction = () => {
      if (!audioArmed) {
        updateAudioIcon();
      }
    };

    window.addEventListener('pointerdown', handleInteraction);
    window.addEventListener('keydown', handleInteraction);

    return () => {
      window.removeEventListener('pointerdown', handleInteraction);
      window.removeEventListener('keydown', handleInteraction);
    };
  }, [audioArmed, updateAudioIcon]);

  // 마지막 사용값 복원
  useEffect(() => {
    const last = localStorage.getItem('lastPlaylist');
    if (last) {
      setUrl(last);
    }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    loadPlaylist(url.trim());
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      loadPlaylist(url.trim());
    }
  };

  // 히스토리 초기화
  const clearHistory = () => {
    setPlaylistHistory([]);
    localStorage.removeItem('playlistHistory');
  };

  return (
    <div className="page-container">
      <div className="container">
        <div className="page-header">
          <h1 className="page-title">
            {language === 'ko' ? '🎵 YouTube 플레이리스트 플레이어' : '🎵 YouTube Playlist Player'}
          </h1>
          <p className="page-subtitle">
            {language === 'ko' 
              ? 'URL을 붙여넣으면 바로 재생되는 AI-LABC Lab의 음악 플레이어'
              : 'AI-LABC Lab\'s music player that plays immediately when you paste a URL'
            }
          </p>
        </div>

        <div className="page-content">
          <div className="youtube-player-container">
            {/* 입력 패널 */}
            <div className="input-panel">
              <h2>
                {language === 'ko' 
                  ? '🎵 YouTube 플레이리스트 붙여넣기 → 바로 재생' 
                  : '🎵 Paste YouTube Playlist → Play Immediately'
                }
              </h2>
              
              <form onSubmit={handleSubmit} className="input-row">
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    language === 'ko'
                      ? '예) https://youtube.com/playlist?list=PLxxxxxxxxx 또는 watch?v=...&list=PL...'
                      : 'e.g.) https://youtube.com/playlist?list=PLxxxxxxxxx or watch?v=...&list=PL...'
                  }
                  className="url-input"
                />
                <button type="submit" className="load-btn">
                  {language === 'ko' ? '불러오기' : 'Load'}
                </button>
                <button type="button" onClick={loadDemo} className="demo-btn">
                  {language === 'ko' ? '데모' : 'Demo'}
                </button>
              </form>
              
              <div className="tip">
                {language === 'ko' 
                  ? isMobile 
                    ? '🔊 버튼으로 소리를 켜고 끌 수 있습니다. 모바일에서는 터치로도 재생이 가능합니다.'
                    : '🔊 버튼으로 소리를 켜고 끌 수 있습니다. 브라우저 정책에 따라 자동재생이 제한될 수 있습니다.'
                  : isMobile
                    ? 'Click the 🔊 button to toggle sound. On mobile, you can also play by touching the screen.'
                    : 'Click the 🔊 button to toggle sound. Autoplay may be restricted by browser policies.'
                }
              </div>
              
              {/* 히스토리 정보 */}
              {playlistHistory.length > 0 && (
                <div className="history-info">
                  <div className="history-header">
                    <span>
                      {language === 'ko' 
                        ? `📚 재생 히스토리 (${playlistHistory.length}개)`
                        : `📚 Play History (${playlistHistory.length})`
                      }
                    </span>
                    <button 
                      onClick={clearHistory}
                      className="clear-history-btn"
                      title={language === 'ko' ? '히스토리 초기화' : 'Clear History'}
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="history-list">
                    {playlistHistory.slice(0, 3).map((playlist, index) => (
                      <div key={playlist.id} className="history-item">
                        <span className="history-title">{playlist.title}</span>
                        <span className="history-count">
                          {language === 'ko' 
                            ? `${playlist.playCount || 1}회 재생`
                            : `${playlist.playCount || 1} plays`
                          }
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* 플레이어 스테이지 */}
            <div className="player-stage">
              <iframe
                ref={playerRef}
                title="YouTube playlist"
                allow="autoplay; encrypted-media; picture-in-picture"
                src=""
                loading="lazy"
                className="youtube-iframe"
              />
              
              {showSoundTip && (
                <div className="sound-tip">
                  {language === 'ko' 
                    ? isMobile
                      ? '🔊 화면을 터치하거나 버튼을 눌러 소리를 켜세요.'
                      : '🔊 화면을 한 번 터치해 소리를 켜세요.'
                    : isMobile
                      ? '🔊 Touch the screen or click the button to turn on sound.'
                      : '🔊 Touch the screen once to turn on sound.'
                  }
                </div>
              )}
              
              <button
                onClick={toggleAudio}
                className={`audio-toggle ${audioOn ? 'audio-on' : 'audio-off'}`}
                aria-pressed={audioOn}
                aria-label={audioOn ? 'Sound on' : 'Sound off'}
              >
                {audioOn ? '🔊' : '🔇'}
              </button>
            </div>

            {/* AI 추천 섹션 */}
            <AIRecommendation 
              language={language}
              playlistHistory={playlistHistory}
              currentSong={currentSong}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default YouTubePlayer;
